import { useDisableTeam } from "@/components/page/disableTeam";
import { /*useNavigate, */ useOutletContext } from "react-router-dom";
import type { MeetingRunContext } from "./MeetingRunContext";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
// import { AppUrlPath } from "@/lib/AppUrlPath";
// import { useEffect } from "react";
import { Chip } from "@repo/ui/components/Base/Chip";
import {
  cn,
  downloadFile,
  formatFileSize,
  openInNewTab,
} from "@repo/shared/utils";
import { LockIcon, PaperclipIcon, UnlinkIcon } from "lucide-react";
// import { TextAreaAuto } from "@repo/ui/components/Base/TextAreaAuto";
import { TextAreaAuto } from "@repo/ui/components/Base/TextAreaAuto";
import { sections } from "./itemSections/sections";
import { useRef } from "react";
import { useHashScroll } from "./useHashScroll";
import {
  agendaItemRepo,
  type AgendaItemDto,
} from "@/repositories/agendaItemRepo";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { documentRepo } from "@/repositories/documentRepo";

export const MeetingRunItem = () => {
  useDisableTeam();
  const outletContext = useOutletContext() as MeetingRunContext;

  const {
    tenantId,
    // meeting,
    // meetingItem,
    agendaItem: _item,
    // itemSection,
    // directionRef,
    mutationSystem,
  } = outletContext;

  const mutationKey = "agendaItem:" + _item!.id;

  const agendaItemMutation: Mutator<AgendaItemDto> = useFutureCallback(
    async (id, data) => {
      await agendaItemRepo.update(tenantId, id, data);
    }
  );
  const agendaItem = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _item!
  );

  const scrollRootRef = useRef<HTMLDivElement>(null);
  useHashScroll(scrollRootRef);

  return (
    <div className="w-full flex flex-col gap-y-2 flex-grow">
      {/* fixed header */}
      <div className="flex-shrink-0">
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-end w-full px-1 pb-2">
          <div className="justify-self-end whitespace-nowrap">Motion text</div>

          <div className="justify-self-end text-center text-base font-medium max-w-[80%] mx-auto break-words leading-snug">
            {agendaItem.name}
          </div>

          <div className="opacity-0 select-none">Motion text</div>
        </div>
        <TextAreaAuto
          id="motion"
          // label="Motion text"
          className="mb-2 min-h-10 h-10"
          value={agendaItem?.motion ?? ""}
          onChange={(evt) => {
            mutationSystem.debounce(
              mutationKey,
              agendaItemMutation,
              { motion: evt.target.value },
              agendaItem.id
            );
          }}
        />
        <div className="mb-4">
          {agendaItem.documents?.map((doc) => {
            const attachment = documentRepo.getLinkedAttachment(doc);
            const downloadable = attachment?.url && !doc.confidential;

            return (
              <Chip
                key={doc.id}
                className={cn(
                  "inline-flex flex-row mr-1",
                  downloadable || "bg-accent hover:bg-accent"
                )}
                icon={
                  doc.confidential
                    ? LockIcon
                    : !attachment?.url
                      ? UnlinkIcon
                      : PaperclipIcon
                }
                onClick={
                  downloadable
                    ? () => {
                        if (!attachment?.url) return;

                        if (attachment.isUrl) {
                          openInNewTab(attachment.url);
                        } else {
                          downloadFile(attachment.url);
                        }
                      }
                    : undefined
                }
              >
                {doc.name}
                {attachment?.url && (
                  <> ({formatFileSize(attachment?.sizeBytes ?? 0)})</>
                )}
              </Chip>
            );
          })}
        </div>
      </div>

      {/* Item sections */}
      <div
        ref={scrollRootRef}
        className="flex flex-col h-0 flex-grow overflow-auto px-2"
      >
        {sections.map((section) => {
          const SectionComponent = section.component;
          return (
            <SectionComponent
              key={section.id}
              id={section.id}
              idSuffix="-meeting-section"
              open={outletContext.openSections.getValueById(section.id)}
              onToggle={(value) =>
                outletContext.openSections.setValueById(section.id, value)
              }
            />
          );
        })}
      </div>
    </div>
  );
};
